import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Input, Space, Spin, Typography, message } from "antd";
import { KeyOutlined, LinkOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { AccountRepositoryImpl } from "../../Modules/Account/Data/repositories/AccountRepositoryImpl";
import { GetRankTrackerConfigUseCase } from "../../Modules/Account/Domain/UseCase/GetRankTrackerConfigUseCase";
import { UpdateRankTrackerConfigUseCase } from "../../Modules/Account/Domain/UseCase/UpdateRankTrackerConfigUseCase";
import { selectUser } from "../../Modules/User/Presentation/selectors";
import { useAppSelector } from "../../Modules/Presentation/hooks";
import { isValidRankTrackerUrl } from "../../Utils/rankTrackerSettings";

const { Title, Text } = Typography;

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const HeaderCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
`;

const SettingCard = styled(Card)`
  border-radius: 8px;
`;

const Hint = styled(Text)`
  display: block;
  margin-top: 8px;
  line-height: 1.5;
`;

const SettingsPage: React.FC = () => {
  const currentUser = useAppSelector(selectUser);
  const repository = useMemo(() => new AccountRepositoryImpl(), []);
  const [rankTrackerUrl, setRankTrackerUrl] = useState("");
  const [rankTrackerToken, setRankTrackerToken] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const config = await new GetRankTrackerConfigUseCase(repository).execute();
      setRankTrackerUrl(config.request_url ?? "");
      setRankTrackerToken(config.token ?? "");
      setUpdatedAt(config.updated_at);
    } catch {
      message.error("Rank Tracker 설정을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [repository]);

  useEffect(() => {
    if (currentUser?.grade === "ADMIN") {
      void loadSettings();
    } else {
      setLoading(false);
    }
  }, [currentUser?.grade, loadSettings]);

  if (!currentUser || currentUser.grade !== "ADMIN") {
    return (
      <PageContainer>
        <Alert
          message="접근 권한이 없습니다"
          description="설정 페이지는 관리자만 접근할 수 있습니다."
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      </PageContainer>
    );
  }

  const handleSave = async () => {
    const value = rankTrackerUrl.trim();
    const token = rankTrackerToken.trim();

    if (!value) {
      message.error("Rank Tracker URL을 입력해주세요.");
      return;
    }

    if (!isValidRankTrackerUrl(value)) {
      message.error(
        "http:// 또는 https://로 시작하는 올바른 URL을 입력해주세요.",
      );
      return;
    }

    if (!token) {
      message.error("Rank Tracker token을 입력해주세요.");
      return;
    }

    setSaving(true);
    try {
      const config = await new UpdateRankTrackerConfigUseCase(repository).execute({
        request_url: value,
        token,
      });
      setRankTrackerUrl(config.request_url ?? value);
      setRankTrackerToken(config.token ?? token);
      setUpdatedAt(config.updated_at);
      message.success("Rank Tracker URL과 token이 저장되었습니다.");
    } catch {
      message.error("Rank Tracker 설정 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <HeaderCard>
        <Title level={2} style={{ margin: 0 }}>
          설정
        </Title>
        <Text type="secondary">서비스에서 사용할 외부 도구를 설정합니다.</Text>
      </HeaderCard>

      <SettingCard title="Rank Tracker 설정">
        <Spin spinning={loading}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Input
                id="rank-tracker-url"
                size="large"
                prefix={<LinkOutlined />}
                placeholder="https://rank-tracker.example.com"
                value={rankTrackerUrl}
                onChange={(event) => setRankTrackerUrl(event.target.value)}
                onPressEnter={handleSave}
                disabled={loading || saving}
              />
              <Hint type="secondary">
                순위 추적 도구의 접속 주소를 입력하세요. 저장된 값은 slot project DB에서 관리자 공용 설정으로 관리됩니다.
              </Hint>
            </div>

            <div>
              <Input.Password
                id="rank-tracker-token"
                size="large"
                prefix={<KeyOutlined />}
                placeholder="Rank Tracker token"
                value={rankTrackerToken}
                onChange={(event) => setRankTrackerToken(event.target.value)}
                onPressEnter={handleSave}
                disabled={loading || saving}
              />
              <Hint type="secondary">
                Rank Tracker API token을 입력하세요. URL과 함께 관리자 공용 설정으로 저장됩니다.
              </Hint>
            </div>

            <Space wrap>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={loading}
              >
                저장
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadSettings} disabled={loading || saving}>
                새로고침
              </Button>
              {rankTrackerUrl && isValidRankTrackerUrl(rankTrackerUrl) && (
                <Button
                  icon={<LinkOutlined />}
                  href={rankTrackerUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  열어보기
                </Button>
              )}
            </Space>
            {updatedAt && (
              <Text type="secondary">
                마지막 수정: {new Date(updatedAt).toLocaleString("ko-KR")}
              </Text>
            )}
          </Space>
        </Spin>
      </SettingCard>
    </PageContainer>
  );
};

export default SettingsPage;
